import { makeStyles, useTranslation } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions, useUser, useUserPermissions } from '@paintscout/ui-business'
import AddBoxIcon from '@material-ui/icons/AddBox'
import ArchiveIcon from '@material-ui/icons/Archive'
import EditIcon from '@material-ui/icons/Edit'
import CopyIcon from '@material-ui/icons/FileCopy'
import ListIcon from '@material-ui/icons/List'
import ConvertIcon from '@material-ui/icons/SwapHoriz'
import get from 'lodash/get'
import React, { useMemo } from 'react'
import type { RenderableItem } from '@paintscout/util/builder'
import { getFeatures, getItems, getObjectLabels, getQuoteOptions } from '@paintscout/util/builder'
import { canSaveItemToSettings, saveToSettingsAction } from '../util/saveToSettingsAction'
import { useQuote } from '../context'
import HideAmountButton from './HideAmountButton'
import ItemRowContent from './ItemRowContent'
import OutlinedItemTable from './OutlinedItemTable'
import type { OutlinedItemTableProps } from './OutlinedItemTable'

export interface OptionsTableNextProps {
  collapsed?: boolean
  selectedItems?: OutlinedItemTableProps['selectedItems']
  onItemSelection?: OutlinedItemTableProps['onItemSelection']
  onItemAction: (action: string, section: 'options', item: RenderableItem) => void
  onToggle?: (ev: React.MouseEvent) => any
  showAddButton?: boolean
  inPresentation?: boolean
}

const useStyles = makeStyles(
  (_theme: Theme) => ({
    root: {},
    hidePrint: {
      '@media print': {
        display: 'none'
      }
    }
  }),
  { name: 'OptionsTableNext' }
)

/**
 * The "Options" section for the new (estimateNext) pipeline, ported from `OptionsTable` onto the
 * shared outlined primitives. A single "solo" header strip (grey + skinny) holds the section title,
 * the select-all checkbox, and the Hide toggle — there's no separate "Item" column band.
 */
export default function OptionsTableNext({
  collapsed,
  selectedItems,
  onItemSelection,
  onItemAction,
  onToggle,
  showAddButton
}: OptionsTableNextProps) {
  const classes = useStyles()
  const { __ } = useTranslation()
  const { isCrew, isOfficeUser } = useUser()
  const { permissionToWriteAllDocuments, permissionToEditAccountSettings } = useUserPermissions()
  const { quote, tableView, onReorder, onAddItem, isEditable, canAddAdditionalWork, isCustomerView } = useQuote()
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })
  const features = getFeatures({ options })
  const hasAdditionalWork = get(features, 'quotes.additionalWork')
  const canConvertToLineItem = get(features, 'quotes.convertToLineItem')
  const hasOptionsApproval = options?.options?.optionsApproval?.enabled

  const { addIndex, hideAllPrices } = getQuoteOptions({ quote, options })
  const { allowQuoteAdditionalWork } = getQuoteOptions({ options })

  const items: RenderableItem[] = useMemo(
    () =>
      getItems({
        quote,
        section: 'options',
        view: tableView,
        consumer: 'customer',
        options,
        overrideOptions: { showPrices: true }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quote, tableView, options]
  )

  if (items.length === 0 && !canAddAdditionalWork) {
    return null
  }

  function handleReorder({ items, addIndex }) {
    onReorder({ items, section: 'options', view: tableView, addIndex })
  }

  const canShowAddButton = canAddAdditionalWork && showAddButton
  const useCheckboxes = !!(canAddAdditionalWork && onItemSelection)

  // Customer-facing nudge only — in the editor the estimator already knows, and the strip there has
  // to leave room for the Hide toggle.
  const sectionSubtitle = isCustomerView
    ? __('Select items below to add to your %s', objectLabels.quote.value.toLowerCase())
    : undefined

  return (
    <div className={classes.root}>
      <OutlinedItemTable
        sectionTitle={objectLabels.option.plural}
        sectionSubtitle={sectionSubtitle}
        largeSectionTitle={isCustomerView}
        sectionAction={
          // Nothing to hide until the section holds an option (ENG-1478).
          canAddAdditionalWork && items.length > 0 ? (
            <HideAmountButton visible={!collapsed} visibleLabel="Hide" hiddenLabel="Show" onToggle={onToggle} />
          ) : undefined
        }
        collapsed={collapsed}
        hideHeader
        draggable={!!isEditable}
        checkboxes={useCheckboxes}
        selectedItems={selectedItems}
        items={items}
        addIndex={addIndex.options}
        addButtonLabel={`Add ${objectLabels.option.value}`}
        showAddButton={canShowAddButton}
        section={'options'}
        onItemSelection={onItemSelection}
        onItemClick={
          (!quote.is_invoice || permissionToWriteAllDocuments) && canAddAdditionalWork
            ? (_ev, item) => onItemAction('edit', 'options', item as RenderableItem)
            : undefined
        }
        singleBorder
        onAddClick={(_ev, insertAfterKey, type) => onAddItem('options', insertAfterKey, type)}
        onReorder={handleReorder}
        renderRowColumns={(item: RenderableItem) => {
          const actions = []
          actions.push({ key: 'edit', label: 'Edit', icon: EditIcon })
          if ((!isCrew && !isOfficeUser && !quote.is_invoice) || !hasAdditionalWork) {
            actions.push({ key: 'move-to-bid', label: `Move to ${objectLabels.quote.value}`, icon: AddBoxIcon })
          }
          if (!quote.is_invoice && hasAdditionalWork && allowQuoteAdditionalWork) {
            actions.push({ key: 'move-to-pending', label: `Move to Additional Work`, icon: AddBoxIcon })
          }
          if (quote.is_invoice && hasAdditionalWork) {
            actions.push({
              key: 'move-to-pending',
              label: `Move to ${objectLabels.additionalWork.value}`,
              icon: AddBoxIcon
            })
          }
          if (!quote.is_invoice && hasOptionsApproval) {
            actions.push({ key: 'move-to-added-options', label: `Move to Added Options`, icon: AddBoxIcon })
          }
          actions.push({ key: 'archive', label: 'Archive', icon: ArchiveIcon })
          if (tableView === 'area' && item.type !== 'group') {
            actions.push({ key: 'copy', label: 'Duplicate', icon: CopyIcon })
          }
          if (canConvertToLineItem && item.type !== 'line_item') {
            actions.push({ key: 'convert', label: 'Convert to Line Item', icon: ConvertIcon })
          }
          if (canSaveItemToSettings({ permissionToEditAccountSettings, item })) {
            actions.push(saveToSettingsAction)
          }
          if (item.type === 'group') {
            actions.push({ key: 'ungroup', label: 'Ungroup', icon: ListIcon })
          }

          return (
            <ItemRowContent
              item={item}
              section={'options'}
              isEditable={canAddAdditionalWork}
              showAmount={!hideAllPrices}
              actions={canAddAdditionalWork ? actions : undefined}
              onAction={(action, it) => onItemAction(action, 'options', it)}
            />
          )
        }}
      />
    </div>
  )
}
